import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Megaphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AnnouncementTicker — right-to-left scrolling ticker of announcement titles.
 * Pinned first, then newest. Pauses on hover. Each item opens the Announcements tab.
 */
export default function AnnouncementTicker() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/announcements`, { headers: getAuthHeader() });
        if (!r.ok || cancelled) return;
        const d = await r.json();
        // Backend already returns visible-only for viewers, sorted pinned-first newest-first.
        setItems((d.announcements || []).filter(a => !a.hidden && !a.expired));
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  // Freeze the ticker whenever ANY modal/dialog is open over the dashboard
  // (survey popup, announcement popup, or any other Radix dialog).
  useEffect(() => {
    const check = () => {
      const openModal = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
      );
      setPopupOpen(!!openModal);
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });
    return () => obs.disconnect();
  }, []);

  if (items.length === 0) return null;

  // Duplicate the list so the marquee loops seamlessly.
  const loop = [...items, ...items];

  return (
    <div className="ticker-wrap flex items-center gap-3 rounded-lg border bg-card px-3 py-2 overflow-hidden" data-testid="announcement-ticker">
      <div className="flex items-center gap-1.5 shrink-0 text-primary font-semibold text-xs uppercase tracking-wider pr-3 border-r">
        <Megaphone className="h-4 w-4" /> News
      </div>
      <div className="ticker-viewport flex-1 overflow-hidden">
        <div className={`ticker-track flex items-center gap-10 whitespace-nowrap ${popupOpen ? 'ticker-paused' : ''}`}>
          {loop.map((a, i) => (
            <button
              key={`${a.announcement_id}-${i}`}
              type="button"
              onClick={() => navigate(`/announcements?open=${a.announcement_id}`)}
              className="text-sm text-foreground/80 hover:text-primary transition-colors inline-flex items-center gap-2"
              data-testid={`ticker-item-${a.announcement_id}`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-primary/60 shrink-0" />
              {a.is_new && <span className="text-[10px] font-bold text-red-600">NEW</span>}
              {a.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
